import { useCallback } from 'react';

import {
  useDirectoryGetProductionCalendar,
  useDirectoryUpdateProductionCalendar,
} from 'api/requests';
import { formatToISODate, getUtcTime, lodash } from 'helpers';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form';
import { useDirectoryLockAndUnlockProductionCalendar } from 'modules/directory/common/hooks';

import { ProductionCalendarFormType } from '../forms/productionCalendarForm';

export const useProductionCalendar = () => {
  const { loading, productionCalendar, error } =
    useDirectoryGetProductionCalendar();

  const {
    updateProductionCalendar,
    error: submitError,
    loading: submitLoading,
  } = useDirectoryUpdateProductionCalendar();

  const weekendsDate: Date[] = lodash
    .compact(productionCalendar?.nonBusinessDays)
    .map((date) => {
      const res = getUtcTime(new Date(date));

      return res;
    });

  const { lockDirectoryIfPossible, unlockDirectory } =
    useDirectoryLockAndUnlockProductionCalendar({
      id: productionCalendar?.id,
      versionNumber: productionCalendar?.versionNumber,
    });

  const onSubmit = useCallback<FormSubmit<ProductionCalendarFormType>>(
    async (values, helpers) => {
      await updateProductionCalendar(
        productionCalendar?.id!,
        productionCalendar?.versionNumber!,
        values.weekendsDate?.map((date) => formatToISODate(date)) ?? []
      );
      helpers.setReadOnly(true);
    },
    [
      productionCalendar?.id,
      productionCalendar?.versionNumber,
      updateProductionCalendar,
    ]
  );

  // Enable edit mode
  const onEnableEdit = useCallback(
    async (onCallback: () => void) => {
      const isPossibleEdit = await lockDirectoryIfPossible?.();
      if (isPossibleEdit) {
        onCallback();
      }
    },
    [lockDirectoryIfPossible]
  );

  // Disable edit mode
  const onDisableEdit = useCallback(
    async (onCallback: () => void) => {
      unlockDirectory?.();
      onCallback();
    },
    [unlockDirectory]
  );

  useServerErrorNotify(error || submitError);

  return {
    weekendsDate,
    loading,
    submitLoading,
    onEnableEdit,
    onDisableEdit,
    onSubmit,
  };
};
